import React, { useState, useMemo, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Table,
  Statistic,
  Button,
  Spin,
  Typography,
  Tag,
  Input,
  Space,
  Segmented,
  Result,
  Tooltip,
  message,
} from "antd";
import {
  ReloadOutlined,
  DownloadOutlined,
  SearchOutlined,
  DollarOutlined,
  CalendarOutlined,
  TeamOutlined,
  PieChartOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { useIsMobile } from "../../../hooks/useIsMobile";
import useModuleRevenueData from "./useModuleRevenueData";
import {
  formatCurrency,
  formatPercentage,
  formatRelativeTime,
  formatNumber,
  downloadCSV,
  calculateKPIs,
  prepareDonutChartData,
} from "./utils";

// Register Chart.js components
ChartJS.register(ArcElement, ChartTooltip, Legend);

const { Title, Text } = Typography;

// Chart colors - pastel/professional palette
const CHART_COLORS = [
  "#3D5864",
  "#66A0BC",
  "#91C6E7",
  "#B6E3FF",
  "#D8C3A5",
  "#E98074",
  "#E85A4F",
  "#8E8D8A",
  "#9BC53D",
  "#5D5C61",
];

const ModuleRevenue = () => {
  const isMobile = useIsMobile();
  const { loading, recalculating, data, lastCalculatedAt, recalculate } =
    useModuleRevenueData();

  // Filter states
  const [searchText, setSearchText] = useState("");
  const [moduleTypeFilter, setModuleTypeFilter] = useState("all");
  const [chartMetric, setChartMetric] = useState("mrr");

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Handle search with debounce
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchText(value);

    // Simple debounce
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, []);

  // Filter data based on search and module type
  const filteredData = useMemo(() => {
    if (!data) return [];

    return data.filter((item) => {
      // Search filter
      const searchLower = debouncedSearch.toLowerCase();
      const matchesSearch =
        !debouncedSearch ||
        (item.module_name || "").toLowerCase().includes(searchLower) ||
        (item.module_sku || "").toLowerCase().includes(searchLower);

      // Module type filter
      let matchesType = true;
      if (moduleTypeFilter === "product_lines") {
        matchesType = item.is_product_line === true;
      } else if (moduleTypeFilter === "addons") {
        matchesType = item.is_product_line !== true;
      }

      return matchesSearch && matchesType;
    });
  }, [data, debouncedSearch, moduleTypeFilter]);

  // Calculate KPIs from filtered data
  const kpis = useMemo(() => calculateKPIs(filteredData), [filteredData]);

  // Prepare chart data
  const chartData = useMemo(
    () => prepareDonutChartData(filteredData, chartMetric, CHART_COLORS),
    [filteredData, chartMetric]
  );

  // Chart options
  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: isMobile ? "bottom" : "right",
          labels: {
            boxWidth: 12,
            padding: 10,
            font: { size: 11 },
          },
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const label = context.label || "";
              const value = context.raw || 0;
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage =
                total > 0 ? ((value / total) * 100).toFixed(1) : 0;
              if (chartMetric === "mrr") {
                return `${label}: ${formatCurrency(value)} (${percentage}%)`;
              }
              return `${label}: ${formatNumber(value)} (${percentage}%)`;
            },
          },
        },
      },
    }),
    [isMobile, chartMetric]
  );

  // Table columns
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: "Modulo",
        dataIndex: "module_name",
        key: "module_name",
        fixed: isMobile ? false : "left",
        render: (text, record) => (
          <Space>
            <Text strong>{text || "-"}</Text>
            {record.is_product_line && <Tag color="blue">Linea</Tag>}
          </Space>
        ),
      },
      {
        title: "SKU",
        dataIndex: "module_sku",
        key: "module_sku",
        responsive: ["md"],
        render: (text) => <Text type="secondary">{text || "-"}</Text>,
      },
      {
        title: "MRR",
        dataIndex: "total_mrr",
        key: "total_mrr",
        defaultSortOrder: "descend",
        sorter: (a, b) => (a.total_mrr || 0) - (b.total_mrr || 0),
        render: (value) => (
          <Text strong style={{ color: "#3D5864" }}>
            {formatCurrency(value)}
          </Text>
        ),
      },
      {
        title: "ARR",
        dataIndex: "total_arr",
        key: "total_arr",
        responsive: ["lg"],
        sorter: (a, b) => (a.total_arr || 0) - (b.total_arr || 0),
        render: (value) => formatCurrency(value),
      },
      {
        title: "Abbonamenti",
        dataIndex: "active_subscriptions_count",
        key: "active_subscriptions_count",
        sorter: (a, b) =>
          (a.active_subscriptions_count || 0) -
          (b.active_subscriptions_count || 0),
        render: (value, record) => {
          const moduleId = record.module_pointer?.objectId;
          if (!moduleId) return formatNumber(value);
          const filters = JSON.stringify([
            {
              id: 1,
              column: "__module__",
              columnName: "Modulo",
              value: moduleId,
              displayValue: record.module_name || moduleId,
              operator: "hasModule",
              logicalOperator: "AND",
            },
          ]);
          return (
            <Link
              to={`/orgs?filters=${encodeURIComponent(filters)}`}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <span style={{ fontWeight: 500 }}>{formatNumber(value)}</span>
              <span style={{ fontSize: 11, color: "#8c8c8c" }}>org</span>
              <RightOutlined style={{ fontSize: 10, color: "#8c8c8c" }} />
            </Link>
          );
        },
      },
      {
        title: "Split M/A",
        key: "split",
        responsive: ["lg"],
        render: (_, record) => {
          const monthly = record.monthly_subscribers_count || 0;
          const yearly = record.yearly_subscribers_count || 0;
          return (
            <Tooltip title={`Mensili: ${monthly} / Annuali: ${yearly}`}>
              <Text>
                {monthly}/{yearly}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        title: "Prezzo Medio",
        dataIndex: "average_price",
        key: "average_price",
        responsive: ["xl"],
        sorter: (a, b) => (a.average_price || 0) - (b.average_price || 0),
        render: (value) => formatCurrency(value),
      },
      {
        title: "Quantità",
        dataIndex: "total_quantity",
        key: "total_quantity",
        responsive: ["xl"],
        sorter: (a, b) => (a.total_quantity || 0) - (b.total_quantity || 0),
        render: (value) => formatNumber(value),
      },
    ];

    return baseColumns;
  }, [isMobile]);

  // Handle CSV export
  const handleExportCSV = useCallback(() => {
    downloadCSV(filteredData, "module_revenue");
    message.success("Export CSV completato!");
  }, [filteredData]);

  // Loading state
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Spin size="large" tip="Caricamento dati..." />
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <Result
        status="info"
        title="Nessun dato disponibile"
        subTitle="Non sono presenti dati di revenue per i moduli. Prova a ricalcolare i dati."
        extra={
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            loading={recalculating}
            onClick={recalculate}
          >
            Ricalcola Ora
          </Button>
        }
      />
    );
  }

  // Mobile card view
  const MobileCardView = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {filteredData.map((item) => (
        <Card key={item.objectId || item.module_sku} size="small">
          <Space direction="vertical" style={{ width: "100%" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text strong>{item.module_name}</Text>
              {item.is_product_line && <Tag color="blue">Linea</Tag>}
            </div>
            <Text type="secondary">{item.module_sku}</Text>
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Statistic
                  title="MRR"
                  value={item.total_mrr}
                  formatter={(v) => formatCurrency(v)}
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
              <Col span={12}>
                {item.module_pointer?.objectId ? (
                  <Link
                    to={`/orgs?filters=${encodeURIComponent(
                      JSON.stringify([{
                        id: 1,
                        column: "__module__",
                        columnName: "Modulo",
                        value: item.module_pointer.objectId,
                        displayValue: item.module_name || item.module_pointer.objectId,
                        operator: "hasModule",
                        logicalOperator: "AND",
                      }])
                    )}`}
                  >
                    <Statistic
                      title={<span>Abbonamenti <RightOutlined style={{ fontSize: 10 }} /></span>}
                      value={item.active_subscriptions_count}
                      formatter={(v) => formatNumber(v)}
                      valueStyle={{ fontSize: 16, color: "#1890ff" }}
                    />
                  </Link>
                ) : (
                  <Statistic
                    title="Abbonamenti"
                    value={item.active_subscriptions_count}
                    formatter={(v) => formatNumber(v)}
                    valueStyle={{ fontSize: 16 }}
                  />
                )}
              </Col>
            </Row>
          </Space>
        </Card>
      ))}
    </div>
  );

  return (
    <div style={{ padding: isMobile ? 12 : 0 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Revenue per Modulo
          </Title>
          {lastCalculatedAt && (
            <Text type="secondary">
              Ultimo aggiornamento: {formatRelativeTime(lastCalculatedAt)}
            </Text>
          )}
        </div>
        <Space wrap>
          <Button
            icon={<ReloadOutlined spin={recalculating} />}
            loading={recalculating}
            onClick={recalculate}
          >
            Ricalcola Ora
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleExportCSV}>
            Esporta CSV
          </Button>
        </Space>
      </div>

      {/* KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic
              title="MRR Totale"
              value={kpis.totalMRR}
              formatter={(value) => formatCurrency(value)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: "#3D5864" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic
              title="ARR Totale"
              value={kpis.totalARR}
              formatter={(value) => formatCurrency(value)}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: "#66A0BC" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic
              title="Moduli Attivi"
              value={kpis.totalSubscriptions}
              formatter={(value) => formatNumber(value)}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic
              title="Split M/A"
              value={`${formatPercentage(kpis.monthlySplit)} / ${formatPercentage(kpis.yearlySplit)}`}
              prefix={<PieChartOutlined />}
              valueStyle={{ fontSize: isMobile ? 16 : 24 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Chart Section */}
      <Card
        title="Distribuzione Revenue"
        style={{ marginBottom: 24 }}
        extra={
          <Segmented
            size="small"
            value={chartMetric}
            onChange={setChartMetric}
            options={[
              { label: "MRR", value: "mrr" },
              { label: "Abbonamenti", value: "subscriptions" },
            ]}
          />
        }
      >
        <div style={{ height: isMobile ? 300 : 350 }}>
          <Doughnut data={chartData} options={chartOptions} />
        </div>
      </Card>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
          <Space wrap>
            <Input
              placeholder="Cerca per nome o SKU..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={handleSearchChange}
              style={{ width: isMobile ? "100%" : 250 }}
              allowClear
            />
            <Segmented
              value={moduleTypeFilter}
              onChange={setModuleTypeFilter}
              options={[
                { label: "Tutti", value: "all" },
                { label: "Linee Prodotto", value: "product_lines" },
                { label: "Add-on", value: "addons" },
              ]}
            />
          </Space>
          <Text type="secondary">
            {filteredData.length} di {data.length} moduli
          </Text>
        </Space>
      </Card>

      {/* Data Table / Mobile Cards */}
      {isMobile ? (
        <MobileCardView />
      ) : (
        <Card>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey={(record) => record.objectId || record.module_sku}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} di ${total} moduli`,
            }}
            scroll={{ x: "max-content" }}
            size="middle"
          />
        </Card>
      )}
    </div>
  );
};

export default ModuleRevenue;
